<script setup lang="ts">
import { computed } from 'vue';
import { useRoute } from 'vue-router';

import type { ITrackerDealForm, ITrackerDealFormComposableOptions } from '@modules/affiliates-approved/submodules/trackers-settings/composables';
import VBackButton from '@components/buttons/VBackButton/VBackButton.vue';
import VCard from '@components/card/VCard/VCard.vue';
import VLoader from '@components/loader/VLoader.vue';
import {

  useTrackerDealForm,
} from '@modules/affiliates-approved/submodules/trackers-settings/composables';
import {
  ADMIN_AFFILIATE_TRACKERS_SETTINGS_SUBAFFILIATE_CONDITION_MAIN_ROUTE_NAME,
  AFFILIATE_SHOW_SUBAFFILIATE_CONDITION_QUERY,
  AFFILIATE_SUBAFFILIATE_SETTINGS,
  TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_SUBAFFILIATE_CONDITION,
} from '@modules/affiliates-approved/submodules/trackers-settings/submodules/subaffiliate-condition/constants';
import {
  TRANSLATION_GROUP_SUBAFFILIATE_HISTORY_CHANGES_EDIT,
} from '@modules/affiliates-approved/submodules/trackers-settings/submodules/subaffiliate-condition/submodules/subaffiliate-history-changes-edit/constants';
import { useFormatMonthDate } from '@modules/core/composables/datetime';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { useI18n } from '@modules/i18n/composables';
import { useQueryClient } from '@tanstack/vue-query';
import { utcDateToLocal } from '@utils/date';
import { isString } from '@utils/guards';

import { useSubaffiliateHistoryChangesUpdateMutation } from '../../mutations';
import { useSubaffiliateHistoryChangesGetQuery } from '../../queries';

const route = useRoute();
const queryClient = useQueryClient();
const { getTranslation } = useI18n();
const { formatMonthDate } = useFormatMonthDate();

const merchantId = computed(() => (isString(route.params.merchantId) ? Number(route.params.merchantId) : 0));
const itemId = computed(() => (isString(route.params.itemId) ? Number(route.params.itemId) : 0));
const affiliateId = computed(() => (isString(route.params.id) ? Number(route.params.id) : 0));

const { data, isLoading } = useSubaffiliateHistoryChangesGetQuery(itemId, merchantId);

const {
  mutateAsync,
  isLoading: isUpdateLoading,
  errors,
  clearError,
} = useSubaffiliateHistoryChangesUpdateMutation();

const formOptions = computed<ITrackerDealFormComposableOptions>(() => ({
  conditions: data.value?.subaffiliate_conditions ?? [],
  isLoading: isLoading.value,
  conditionValue: data.value?.history_of_subaffiliates_change.subaffiliate_condition_id,
  conditionLabel: getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_SUBAFFILIATE_CONDITION, 'FORM_SUBAFFILIATE_CONDITION_ID').value,
  dateLabel: getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_SUBAFFILIATE_CONDITION, 'FORM_DATE').value,
  dateValue: data.value?.history_of_subaffiliates_change.date ? utcDateToLocal(data.value?.history_of_subaffiliates_change.date) : undefined,
}));

const { formSchema } = useTrackerDealForm(formOptions);

async function onSubmit({ condition_id, date }: ITrackerDealForm): Promise<void> {
  try {
    await mutateAsync({
      merchant: merchantId.value,
      history_of_subaffiliates_change: {
        id: itemId.value,
        subaffiliate_condition_id: condition_id,
        date: formatMonthDate(date),
      },
    });

    await queryClient.refetchQueries({ queryKey: [AFFILIATE_SHOW_SUBAFFILIATE_CONDITION_QUERY, affiliateId] });
  } catch {}
}
</script>

<template>
  <VCard v-data-qa-auto-id="'SubaffiliateHistoryChangesEditPage'"
    type="form"
    :title="$getTranslation(TRANSLATION_GROUP_SUBAFFILIATE_HISTORY_CHANGES_EDIT, 'HEADER')"
  >
    <VLoader
      :loading="isLoading"
      min-height="188px"
    >
      <VForm
        :schema="formSchema"
        :submit-btn-label="$getTranslation(TRANSLATION_GROUP_SUBAFFILIATE_HISTORY_CHANGES_EDIT, 'SUBMIT')"
        :readonly="!$hasPermission(AFFILIATE_SUBAFFILIATE_SETTINGS.HISTORY_CHANGES_EDIT)"
        :is-loading="isUpdateLoading"
        :custom-errors="errors"
        @submit="onSubmit"
        @interacted="clearError"
      >
        <template #secondaryBtn>
          <VBackButton
            :label="$getTranslation(TRANSLATION_GROUP_SUBAFFILIATE_HISTORY_CHANGES_EDIT, 'BACK')"
            :to="{
              name: ADMIN_AFFILIATE_TRACKERS_SETTINGS_SUBAFFILIATE_CONDITION_MAIN_ROUTE_NAME,
            }"
          />
        </template>
      </VForm>
    </VLoader>
  </VCard>
</template>
